import { useContext } from 'react'
import useOneOffPaymentWithObjectivesAndTargetBonusAndContractChanges from '../../../../../graphql/one-off-payment/hooks/useOneOffPaymentWithObjectivesAndTargetBonusAndContractChanges'
import { MeetingStepStatus, MeetingStepViewId, MeetingType, ObjectiveStatus, Currency } from '../../../../../types'
import { retrieveContractStartAndEndDateFromContract } from '../../../../profile/contract/utils/contractUtil'
import { AppraisalFlowContext } from '../../shared/AppraisalFlowProvider'
import { updateMeetingStep } from '../../shared/AppraisalMeetingService'
import { calculateNewTargetsData } from '../../shared/util'
import { filterObjectivesByTypeStatusAndRefYear } from '../../../okr/util'
import { useCurrency } from '../../../../../context/currency-context'
import { BonusBetweenAnnualBonusAndProrataDto } from '../../../../../types'
import { toEligibleFlexibleBonusOverride } from '../../shared/flexibleBonusUtil'

export const useContractWithNewTargetsValidator = (
  currentYearFlexibleBonus: Pick<BonusBetweenAnnualBonusAndProrataDto, 'bonusAmount' | 'contractId' | 'contractCurrency'> | null | undefined,
  hasGovernmentJobTitleReferenceLevel = false,
  flexibleBonusUnknownCurrency?: Currency,
): void => {
  const { meeting, meetingTypeSteps } = useContext(AppraisalFlowContext)
  const newTargetsStep = meetingTypeSteps.find((step) => step.viewId === MeetingStepViewId.NewTargets)

  const { pastTargetsReferenceYear, newTargetsReferenceYear } = meeting.targetsCalculationHelper
  const { loading, error, data } = useOneOffPaymentWithObjectivesAndTargetBonusAndContractChanges(
    meeting.employee.id,
    meeting.id,
    pastTargetsReferenceYear,
    newTargetsReferenceYear,
    meeting.employee.currentContract!.legalEntity.country,
    true,
    hasGovernmentJobTitleReferenceLevel,
  )

  const { currencyConversionData } = useCurrency()

  if (!loading && !error && !!data && data.currentContract) {
    const [contractStartDate, contractEndDate] = retrieveContractStartAndEndDateFromContract(data.currentContract)

    const allowedNewTargets = meeting.allowedMonetaryTargets.newTargetsAllowed

    const flexibleBonusOverride = toEligibleFlexibleBonusOverride(
      currentYearFlexibleBonus,
      meeting.employee.currentContract!.currencyForPayments as Currency,
      currencyConversionData,
      data.branchOfficeByEmployee.timezone,
      newTargetsReferenceYear,
      data.newTargetsContractsInYear,
      data.newTargetsTargetBonusOfYear,
      data.contractChangesByEmployee,
    )

    const {
      isMonetaryTargetMissing: tooLessNewTargetBonusProvided,
      tooMuchTargetBonusProvided: tooMuchNewTargetBonusProvided,
      atLeastOneMonetaryTargetWithWrongCurrency,
      unknownCurrenciesForConversion,
    } = calculateNewTargetsData(
      allowedNewTargets,
      data.branchOfficeByEmployee.timezone,
      data.isBonusValidationEnabled,
      flexibleBonusOverride,
      data.objectivesInMeeting,
      data.newTargetsTargetBonusOfYear,
      data.newTargetsSalaryOfYear,
      meeting.employee.currentContract!.currencyForPayments as Currency,
      data.currentContract!.annualSalary ? data.currentContract!.annualSalary.amount : 0,
      data.currentContract!.targetBonus ? data.currentContract!.targetBonus.amount : 0,
      data.contractChangesByEmployee,
      meeting.type === MeetingType.AppraisalUpdate,
      newTargetsReferenceYear,
      contractStartDate,
      currencyConversionData,
      contractEndDate,
      data.newTargetsContractsInYear,
      flexibleBonusUnknownCurrency,
    )

    const monetaryObjectives = filterObjectivesByTypeStatusAndRefYear(
      data.objectivesInMeeting,
      true,
      [ObjectiveStatus.ProposalFromManager, ObjectiveStatus.Approved],
      newTargetsReferenceYear,
    )
    const existingWrongNewTargets = !allowedNewTargets && monetaryObjectives.length > 0

    const hasUnknownCurrenciesForConversion = unknownCurrenciesForConversion.length > 0

    if (
      newTargetsStep &&
      (tooLessNewTargetBonusProvided ||
        tooMuchNewTargetBonusProvided ||
        atLeastOneMonetaryTargetWithWrongCurrency ||
        hasUnknownCurrenciesForConversion) &&
      ![MeetingStepStatus.Todo, MeetingStepStatus.Incomplete].includes(newTargetsStep.status)
    ) {
      updateMeetingStep(newTargetsStep.id, MeetingStepStatus.Incomplete)
    } else if (
      newTargetsStep &&
      !tooLessNewTargetBonusProvided &&
      !tooMuchNewTargetBonusProvided &&
      !atLeastOneMonetaryTargetWithWrongCurrency &&
      !hasUnknownCurrenciesForConversion &&
      ![MeetingStepStatus.Done, MeetingStepStatus.Todo].includes(newTargetsStep.status) &&
      !existingWrongNewTargets
    ) {
      updateMeetingStep(newTargetsStep.id, MeetingStepStatus.Done)
    }
  }
}
